import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

interface ServerPageResult<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  total: number;
  refetch: () => void;
}

interface ServerPageOptions {
  /**
   * Opt-in: only set this when the target endpoint actually understands the `filters`
   * keys as query params and a `sortBy`/`sortDir` pair (e.g. /api/v3/catalogo). When
   * true, real limit/offset pagination is kept even while filters are active, and
   * sort is applied server-side across the whole dataset — not just the current page.
   * Defaults to false: legacy behavior (fetch everything with filters active, sort
   * client-side) for endpoints that don't support server-side filter/sort.
   */
  serverSideFilters?: boolean;
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
}

export function useServerPage<T>(
  endpoint: string,
  page: number,
  pageSize: number = 20,
  search: string = '',
  searchParam: string = 'q',
  filters?: Record<string, string>,
  options?: ServerPageOptions,
): ServerPageResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hasFilters = filters && Object.values(filters).some(v => v);
  const serverSide = options?.serverSideFilters === true;
  // Stable primitive proxy for `filters` so the fetch callback only depends on the
  // object's *values* (not its identity) — and only when serverSide actually needs them,
  // so the legacy (non-opt-in) callers keep their existing "refetch only when hasFilters
  // toggles" behavior untouched.
  const filtersKey = serverSide ? JSON.stringify(filters ?? {}) : '';

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let result: { data: T[]; headers: Headers };
      if (hasFilters && !serverSide) {
        // Fetch all data when filters are active so filtering spans all pages
        result = await api.getWithResponse<T[]>(`${endpoint}?limit=99999&offset=0`);
      } else {
        const offset = (page - 1) * pageSize;
        let qs = search ? `&${searchParam}=${encodeURIComponent(search)}` : '';
        if (serverSide) {
          for (const [key, val] of Object.entries(filters ?? {})) {
            if (val) qs += `&${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
          }
          if (options?.sortKey) qs += `&sortBy=${encodeURIComponent(options.sortKey)}&sortDir=${options.sortDir ?? 'asc'}`;
        }
        result = await api.getWithResponse<T[]>(`${endpoint}?limit=${pageSize}&offset=${offset}${qs}`);
      }
      setData(result.data ?? []);
      const totalH = result.headers.get('X-Total-Count');
      setTotal(totalH ? parseInt(totalH) : 0);
    } catch (e: any) {
      setError(e?.detail || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, page, pageSize, search, searchParam, hasFilters, serverSide, filtersKey, options?.sortKey, options?.sortDir]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, total, refetch: fetch };
}
